import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import {
  TableContainer,
  Box,
  CircularProgress,
  Paper,
  Button,
} from '@material-ui/core'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'
import { MenuButton, Permitted, ConfirmationDialog } from 'components'
import { SharedCollectedFieldsModal } from './'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

const TableSharedCollectedFields = ({
  edit,
  dataProcessId,
  dataTreatmentId,
  sharedCollectedFields,
  refresh,
  isLoading,
  deferMode = false,
  onRemovePending = () => {},
}) => {
  const [selectedSharedCollectedField, setSelectedSharedCollectedField] =
    useState(null)
  const [sharedCollectedFieldToDelete, setSharedCollectedFieldToDelete] =
    useState(null)
  const [modalMode, setModalMode] = useState('edit')
  const [isDeleting, setIsDeleting] = useState(false)

  const canEdit = Boolean(edit)
  const deleteDialogText = sharedCollectedFieldToDelete
    ? `Deseja realmente excluir os dados compartilhados da categoria de titular "${sharedCollectedFieldToDelete?.titularCategory?.name}"?`
    : ''

  const handleEdit = (sharedCollectedField) => {
    setSelectedSharedCollectedField(sharedCollectedField)
    setModalMode('edit')
  }

  const handleDelete = (sharedCollectedField) => {
    setSharedCollectedFieldToDelete(sharedCollectedField)
  }

  const onConfirmDelete = async () => {
    try {
      setIsDeleting(true)

      if (deferMode) {
        onRemovePending(sharedCollectedFieldToDelete)
        setSharedCollectedFieldToDelete(null)
        setIsDeleting(false)
        return
      }

      if (sharedCollectedFieldToDelete?.collectedFields) {
        const sharedCollectedFieldsIds =
          sharedCollectedFieldToDelete.collectedFields.map(
            (field) => field.sharedCollectedFieldId,
          )

        await service.dponet.sharedCollectedFields.destroyAll({
          dataProcessId,
          dataTreatmentId,
          sharedCollectedField: {
            sharedCollectedFieldsIds,
          },
        })
        refresh()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsDeleting(false)
      setSharedCollectedFieldToDelete(null)
    }
  }

  const handleShow = (sharedCollectedField) => {
    setSelectedSharedCollectedField(sharedCollectedField)
    setModalMode('show')
  }

  const handleCloseModal = () => {
    setSelectedSharedCollectedField(null)
  }

  const renderActions = (sharedCollectedField) => {
    if (deferMode) {
      return (
        <Permitted tag={constants.permissions.DATA_PROCESSES.CREATE}>
          <MenuButton>
            <Button
              color="secondary"
              fullWidth
              onClick={() => handleShow(sharedCollectedField)}
            >
              Visualizar
            </Button>
            <Button
              color="secondary"
              fullWidth
              onClick={() => handleDelete(sharedCollectedField)}
            >
              Excluir
            </Button>
          </MenuButton>
        </Permitted>
      )
    }

    if (canEdit) {
      return (
        <Permitted tag={constants.permissions.DATA_PROCESSES.CREATE}>
          <MenuButton>
            <Button
              color="secondary"
              fullWidth
              onClick={() => handleEdit(sharedCollectedField)}
            >
              Editar
            </Button>
            <Button
              color="secondary"
              fullWidth
              onClick={() => handleDelete(sharedCollectedField)}
            >
              Excluir
            </Button>
          </MenuButton>
        </Permitted>
      )
    }

    return (
      <MenuButton>
        <Button
          color="secondary"
          fullWidth
          onClick={() => handleShow(sharedCollectedField)}
        >
          Visualizar
        </Button>
      </MenuButton>
    )
  }

  const buildRowKey = (sharedField, idx) =>
    sharedField.id ||
    sharedField.titularCategory?.id ||
    `${dataTreatmentId}-${idx}`

  return (
    <>
      <ConfirmationDialog
        open={!!sharedCollectedFieldToDelete}
        setOpen={() => setSharedCollectedFieldToDelete(null)}
        text={deleteDialogText}
        actionAcceptButton={onConfirmDelete}
        loading={isDeleting}
      />
      <SharedCollectedFieldsModal
        open={!!selectedSharedCollectedField}
        onClose={handleCloseModal}
        dataProcessId={dataProcessId}
        dataTreatmentId={dataTreatmentId}
        mode={modalMode}
        sharedCollectedField={selectedSharedCollectedField}
        onSuccess={refresh}
      />
      {isLoading ? (
        <Box
          id="loadBox"
          display="flex"
          width="100%"
          minHeight="300px"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table
              size="small"
              emptyMessage="Nenhum dado compartilhado encontrado"
              noWrap
            >
              <TableHead>
                <TableRow>
                  <TableCell width="30%">Categoria do titular</TableCell>
                  <TableCell width="60%">Categorias dos dados</TableCell>
                  <TableCell width="10%">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty(sharedCollectedFields) &&
                  sharedCollectedFields?.map((sharedCollectedField, idx) => {
                    const categories = helpers.dataCollected.getDataCategories(
                      sharedCollectedField.collectedFields,
                    )
                    return (
                      <TableRow key={buildRowKey(sharedCollectedField, idx)}>
                        <TableCell>
                          {sharedCollectedField.titularCategory?.name}
                        </TableCell>
                        <TableCell>
                          <Box whiteSpace="normal">{categories || '-'}</Box>
                        </TableCell>
                        <TableCell>
                          {renderActions(sharedCollectedField)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  )
}

TableSharedCollectedFields.propTypes = {
  edit: PropTypes.bool,
  dataProcessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataTreatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sharedCollectedFields: PropTypes.array,
  refresh: PropTypes.func,
  deferMode: PropTypes.bool,
  onRemovePending: PropTypes.func,
}

export default TableSharedCollectedFields
